"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const react_plotly_js_1 = __importDefault(require("react-plotly.js"));
const fourier_1 = require("./fourier");
const FourierSeriesVisualization = () => {
    const [funcKey, setFuncKey] = (0, react_1.useState)('square');
    const [terms, setTerms] = (0, react_1.useState)(5);
    const [isAnimating, setIsAnimating] = (0, react_1.useState)(false);
    const [animationProgress, setAnimationProgress] = (0, react_1.useState)(0);
    const animationRef = (0, react_1.useRef)(0);
    const startTimeRef = (0, react_1.useRef)(0);
    const func = (0, react_1.useMemo)(() => fourier_1.FOURIER_FUNCTIONS.find(f => f.key === funcKey) || fourier_1.FOURIER_FUNCTIONS[0], [funcKey]);
    // Reset animation when function or terms change
    (0, react_1.useEffect)(() => {
        setAnimationProgress(0);
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
    }, [funcKey, terms]);
    // Animation loop
    (0, react_1.useEffect)(() => {
        if (!isAnimating)
            return;
        const animate = (currentTime) => {
            if (!startTimeRef.current) {
                startTimeRef.current = currentTime;
            }
            const elapsed = currentTime - startTimeRef.current;
            const duration = 3000; // 3 seconds
            const progress = Math.min(elapsed / duration, 1);
            setAnimationProgress(progress);
            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            }
            else {
                setIsAnimating(false);
                setAnimationProgress(0);
                startTimeRef.current = 0;
            }
        };
        animationRef.current = requestAnimationFrame(animate);
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isAnimating]);
    // Determine sampling range (show 2 periods)
    const xMin = -func.period;
    const xMax = func.period;
    const samples = 400;
    const xs = (0, react_1.useMemo)(() => (0, fourier_1.sampleRange)(xMin, xMax, samples), [xMin, xMax, samples]);
    // Actual function values
    const actualYs = (0, react_1.useMemo)(() => xs.map(x => func.f(x)), [xs, func]);
    // Fourier series approximation
    const fourierYs = (0, react_1.useMemo)(() => {
        if (isAnimating) {
            // During animation, interpolate between 0 and full terms
            const animatedTerms = Math.floor(terms * animationProgress);
            return xs.map(x => (0, fourier_1.evaluateFourierSeries)(func, x, animatedTerms));
        }
        return xs.map(x => (0, fourier_1.evaluateFourierSeries)(func, x, terms));
    }, [xs, func, terms, isAnimating, animationProgress]);
    // Individual harmonic components for visualization
    const harmonics = (0, react_1.useMemo)(() => {
        const harmonicData = [];
        for (let n = 1; n <= Math.min(terms, 8); n++) {
            const harmonicYs = xs.map(x => {
                const { a, b } = calculateFourierCoefficients(func, n);
                return a * Math.cos(2 * Math.PI * n * x / func.period) +
                    b * Math.sin(2 * Math.PI * n * x / func.period);
            });
            harmonicData.push({
                x: xs,
                y: harmonicYs,
                type: 'scatter',
                mode: 'lines',
                line: {
                    color: `hsl(${240 + n * 30}, 70%, 60%)`,
                    width: 1,
                    dash: 'dot'
                },
                name: `Harmonic ${n}`,
                visible: 'legendonly',
                hovertemplate: `Harmonic ${n}<br>x=%{x:.2f}<br>y=%{y:.3f}<extra></extra>`
            });
        }
        return harmonicData;
    }, [xs, func, terms]);
    const handleStartAnimation = () => {
        setIsAnimating(true);
        setAnimationProgress(0);
        startTimeRef.current = 0;
    };
    return (react_1.default.createElement("div", { className: "visualization" },
        react_1.default.createElement("h2", { className: "section-heading", style: { marginTop: 0 } }, "Fourier Series"),
        react_1.default.createElement("p", { style: { color: 'var(--muted)', marginBottom: 16 } }, "Watch how periodic functions are approximated by summing sine and cosine waves. The Fourier series converges to the original function as more terms are added."),
        react_1.default.createElement("section", { className: "panel params", "aria-label": "Graph parameters" },
            react_1.default.createElement("div", { className: "params-grid" },
                react_1.default.createElement("label", { className: "field" },
                    react_1.default.createElement("span", { className: "label" }, "Function"),
                    react_1.default.createElement("select", { value: funcKey, onChange: (e) => setFuncKey(e.target.value) }, fourier_1.FOURIER_FUNCTIONS.map((f) => (react_1.default.createElement("option", { key: f.key, value: f.key }, f.label)))),
                    func.note && react_1.default.createElement("small", { className: "hint" }, func.note)),
                react_1.default.createElement("label", { className: "field" },
                    react_1.default.createElement("span", { className: "label" }, "Terms (n)"),
                    react_1.default.createElement("input", { type: "range", min: 1, max: 20, step: 1, value: terms, onChange: (e) => setTerms(parseInt(e.target.value, 10)) }),
                    react_1.default.createElement("div", { className: "value" }, terms)),
                react_1.default.createElement("label", { className: "field" },
                    react_1.default.createElement("button", { onClick: handleStartAnimation, disabled: isAnimating, style: {
                            padding: '8px 16px',
                            background: isAnimating ? 'var(--muted)' : 'var(--brand)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isAnimating ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                        } }, isAnimating ? 'Animating...' : 'Animate Convergence')))),
        react_1.default.createElement(react_plotly_js_1.default, { data: [
                {
                    x: xs,
                    y: actualYs,
                    type: 'scatter',
                    mode: 'lines',
                    line: { color: '#22d3ee', width: 3 },
                    name: `Actual: ${func.label}`,
                    hovertemplate: 'x=%{x:.2f}<br>f(x)=%{y:.3f}<extra></extra>'
                },
                {
                    x: xs,
                    y: fourierYs,
                    type: 'scatter',
                    mode: 'lines',
                    line: { color: '#a78bfa', width: 2 },
                    name: `Fourier (n=${terms})`,
                    hovertemplate: 'x=%{x:.2f}<br>S(x)=%{y:.3f}<extra></extra>'
                },
                ...harmonics
            ], layout: {
                paper_bgcolor: 'rgba(0,0,0,0)',
                plot_bgcolor: 'rgba(0,0,0,0)',
                font: { color: '#e5e7eb' },
                margin: { l: 48, r: 16, t: 16, b: 36 },
                xaxis: {
                    gridcolor: 'rgba(148,163,184,0.15)',
                    title: 'x',
                    range: [xMin, xMax]
                },
                yaxis: {
                    gridcolor: 'rgba(148,163,184,0.15)',
                    title: 'f(x)',
                    range: [-1.5, 1.5]
                },
                legend: {
                    bgcolor: 'rgba(2,6,23,0.6)',
                    bordercolor: 'var(--border)',
                    borderwidth: 1,
                    x: 1,
                    xanchor: 'right',
                    y: 1
                },
                title: {
                    text: isAnimating ?
                        `Convergence Animation (${Math.floor(terms * animationProgress)}/${terms} terms)` :
                        'Fourier Series Approximation',
                    font: { size: 16, color: '#e5e7eb' }
                }
            }, config: {
                responsive: true,
                displaylogo: false,
                modeBarButtonsToRemove: ['toImage'],
                scrollZoom: true
            }, style: { width: '100%', height: 500 } }),
        react_1.default.createElement("section", { className: "panel", style: { marginTop: 16 } },
            react_1.default.createElement("h3", { className: "section-heading", style: { marginTop: 0 } }, "What is a Fourier Series?"),
            react_1.default.createElement("p", { style: { color: 'var(--muted)' } }, "A Fourier series decomposes a periodic function into a sum of sine and cosine waves:"),
            react_1.default.createElement("pre", { style: { overflowX: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', padding: 12, borderRadius: 8 } }, `f(x) = a₀/2 + Σ(aₙcos(nωx) + bₙsin(nωx))`),
            react_1.default.createElement("ul", { style: { color: 'var(--muted)' } },
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "a\u2080/2"),
                    ": The DC component (average value)"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "a\u2099, b\u2099"),
                    ": Fourier coefficients calculated by integrating the function"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "n"),
                    ": Harmonic number (frequency multiple)"),
                react_1.default.createElement("li", null,
                    react_1.default.createElement("strong", null, "\u03C9"),
                    ": Fundamental frequency (2\u03C0/period)")),
            react_1.default.createElement("p", { style: { color: 'var(--muted)' } }, "More terms generally improve the approximation, especially for smooth functions. Discontinuous functions like the square wave show the Gibbs phenomenon near jumps."))));
};
// Helper function to calculate Fourier coefficients (moved from fourier.ts for component use)
function calculateFourierCoefficients(func, n) {
    const period = func.period;
    const samples = 1000;
    const dx = period / samples;
    let a = 0;
    let b = 0;
    for (let i = 0; i < samples; i++) {
        const x = i * dx;
        const y = func.f(x);
        if (n === 0) {
            a += y;
        }
        else {
            a += y * Math.cos(2 * Math.PI * n * x / period);
            b += y * Math.sin(2 * Math.PI * n * x / period);
        }
    }
    a *= dx / period;
    b *= dx / period;
    return { a, b };
}
exports.default = FourierSeriesVisualization;
